import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Spinner,
  Row,
  Col,
  ToastContainer,
  Toast,
} from "react-bootstrap";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaPhone,
  FaGlobe,
  FaShoppingCart,
  FaLock,
  FaSignInAlt,
} from "react-icons/fa";
import { MdTempleBuddhist } from "react-icons/md";
import { connect } from "react-redux";
import { addToCart, clearCart } from "../redux/actions/cartActions";
import { buddhismAPI } from "../utils/api";

const BuddhismDetail = ({ addToCart, clearCart, cartItemCount }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [temple, setTemple] = useState(location.state?.temple || null);
  const [loading, setLoading] = useState(!location.state?.temple);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [expandedDescription, setExpandedDescription] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const headerRef = useRef(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  const [itemQuantities, setItemQuantities] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth < 768);
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setHeaderVisible(window.scrollY > 250);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    setIsAuthenticated(!!token);

    if (location.state?.temple) {
      setTemple(location.state.temple);
      processTempleData(location.state.temple);
      setLoading(false);
      return;
    }
    fetchTempleData();
  }, [id, location.state]);

  const fetchTempleData = async () => {
    try {
      setLoading(true);
      const response = await buddhismAPI.getById(id);
      const data = response.data;
      setTemple(data);
      processTempleData(data);
    } catch (err) {
      setError("사찰 정보를 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const processTempleData = (data) => {
    if (data.item_groups && data.item_groups.length > 0) {
      setActiveTab(data.item_groups[0].name);
    } else {
      setActiveTab("info");
    }
  };

  const handleLogin = () => navigate("/login", { state: { from: `/buddhism/${id}` } });
  const handleGoBack = () => navigate(-1);
  const handleGoToCart = () => (isAuthenticated ? navigate("/cart") : handleLogin());

  const handleQuantityChange = (itemId, change) => {
    setItemQuantities((prev) => {
      const newQty = Math.max(1, (prev[itemId] || 1) + change);
      return { ...prev, [itemId]: newQty };
    });
  };

  const handleAddToCart = (item) => {
    if (!isAuthenticated) {
      setToastMessage("장바구니 이용을 위해 로그인이 필요합니다.");
      setShowToast(true);
      setTimeout(handleLogin, 1500);
      return;
    }

    const existingCartItems = JSON.parse(localStorage.getItem("cart") || "[]");
    if (existingCartItems.length > 0 && existingCartItems[0].templeId !== temple.id) {
      setToastMessage("장바구니에는 같은 사찰의 상품만 담을 수 있습니다.");
      setShowToast(true);
      if (window.confirm("다른 사찰의 상품이 있습니다. 장바구니를 비우고 새 상품을 담으시겠습니까?")) {
        clearCart();
        const cartItem = { ...item, templeName: temple.name, templeId: temple.id, quantity: itemQuantities[item.id] || 1 };
        addToCart(cartItem, cartItem.quantity);
        setToastMessage(`기존 장바구니가 비워지고 ${item.name}이(가) 추가되었습니다.`);
        setShowToast(true);
      }
      return;
    }

    const quantity = itemQuantities[item.id] || 1;
    const cartItem = { ...item, templeName: temple.name, templeId: temple.id, quantity };
    addToCart(cartItem, quantity);
    setToastMessage(`${item.name}이(가) 장바구니에 추가되었습니다.`);
    setShowToast(true);
  };

  /* ── 로딩 ── */
  if (loading) {
    return (
      <div className="bd-loading">
        <Spinner animation="border" style={{ color: "var(--main-color)", width: "2.5rem", height: "2.5rem" }} />
        <p>사찰 정보를 불러오는 중입니다…</p>
      </div>
    );
  }

  /* ── 오류 ── */
  if (error) {
    return (
      <Container className="py-5">
        <div className="bd-status-box bd-status-box--error">
          <span className="bd-status-ico">⚠</span>
          <p>{error}</p>
          <button className="bd-btn bd-btn--outline" onClick={() => navigate("/pray")}>
            목록으로 돌아가기
          </button>
        </div>
      </Container>
    );
  }

  /* ── 데이터 없음 ── */
  if (!temple) {
    return (
      <Container className="py-5">
        <div className="bd-status-box">
          <span className="bd-status-ico bd-status-ico--dharma">☸</span>
          <p>사찰 정보를 찾을 수 없습니다.</p>
          <button className="bd-btn bd-btn--outline" onClick={() => navigate("/pray")}>
            목록으로 돌아가기
          </button>
        </div>
      </Container>
    );
  }

  const isLongDescription = temple.chief_monk && temple.chief_monk.length > 80;
  const displayDescription =
    !expandedDescription && isLongDescription
      ? `${temple.chief_monk.substring(0, 80)}…`
      : temple.chief_monk;

  const images = temple.images || [];

  /* ── 렌더 ── */
  return (
    <div className={`bd-wrap${isMobile ? " bd-wrap--mobile" : ""}`}>

      {/* 스크롤 시 나타나는 상단 헤더 */}
      <div
        ref={headerRef}
        className="bd-sticky-header"
        style={{ opacity: headerVisible ? 1 : 0, pointerEvents: headerVisible ? "auto" : "none" }}
      >
        <Container className="bd-sticky-inner">
          <button className="bd-back-btn" onClick={handleGoBack}>
            <FaArrowLeft />
          </button>
          <span className="bd-sticky-name">{temple.name}</span>
          <button className="bd-cart-btn" onClick={handleGoToCart}>
            <FaShoppingCart />
            {!isAuthenticated && <FaLock className="bd-cart-lock" />}
            {isAuthenticated && cartItemCount > 0 && (
              <span className="bd-cart-badge">{cartItemCount}</span>
            )}
          </button>
        </Container>
      </div>

      {/* 이미지 갤러리 */}
      <div className="bd-gallery">
        <button className="bd-gallery-back" onClick={handleGoBack} aria-label="뒤로가기">
          <FaArrowLeft />
        </button>

        {images.length > 0 ? (
          <div className="bd-gallery-slider">
            <div
              className="bd-gallery-track"
              style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
            >
              {images.map((img, i) => (
                <div
                  key={i}
                  className="bd-gallery-slide"
                  style={{ backgroundImage: `url(${img.url || img})` }}
                />
              ))}
            </div>

            {images.length > 1 && (
              <>
                <button
                  className="bd-gallery-ctrl bd-gallery-ctrl--prev"
                  onClick={() => setCarouselIndex((c) => Math.max(0, c - 1))}
                  disabled={carouselIndex === 0}
                >
                  <FaChevronLeft />
                </button>
                <button
                  className="bd-gallery-ctrl bd-gallery-ctrl--next"
                  onClick={() => setCarouselIndex((c) => Math.min(images.length - 1, c + 1))}
                  disabled={carouselIndex === images.length - 1}
                >
                  <FaChevronRight />
                </button>
                <div className="bd-gallery-dots">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      className={`bd-gallery-dot${i === carouselIndex ? " bd-gallery-dot--active" : ""}`}
                      onClick={() => setCarouselIndex(i)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="bd-gallery-placeholder">
            <MdTempleBuddhist />
          </div>
        )}
      </div>

      {/* 본문 */}
      <Container className="bd-body">

        {/* 사찰 기본 정보 카드 */}
        <div className="bd-card">
          <div className="bd-card-head">
            <div>
              {temple.type && <span className="bd-type-badge">{temple.type}</span>}
              <h1 className="bd-temple-name">
                {temple.name}
                {temple.is_preparing && <span className="bd-type-badge" style={{marginLeft:'8px', borderColor:'#856404', color:'#856404'}}>준비중</span>}
              </h1>
              {temple.address && (
                <p className="bd-addr">
                  <FaMapMarkerAlt className="bd-addr-ico" />
                  {temple.address}
                </p>
              )}
            </div>
            {/* 데스크탑 장바구니 버튼 */}
            {!isMobile && (
              <button className="bd-btn bd-btn--primary" onClick={handleGoToCart}>
                <FaShoppingCart />
                {isAuthenticated ? "장바구니 보기" : "로그인 후 이용"}
              </button>
            )}
          </div>

          {/* 사찰 소개 */}
          {temple.chief_monk && (
            <div className="bd-desc-wrap">
              <h2 className="bd-section-label">☸ 사찰 소개</h2>
              <div className="bd-desc-box">
                <p className="bd-desc-text">{displayDescription}</p>
                {isLongDescription && (
                  <button
                    className="bd-desc-toggle"
                    onClick={() => setExpandedDescription((v) => !v)}
                  >
                    {expandedDescription ? "접기 ▲" : "더보기 ▼"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* 비로그인 안내 */}
          {!isAuthenticated && (
            <div className="bd-login-notice">
              <FaLock className="bd-login-notice-ico" />
              <span>
                상품 담기는{" "}
                <Link to="/login" className="bd-login-link">로그인</Link>
                {" "}후 이용 가능합니다.
              </span>
            </div>
          )}

          {/* 탭 네비게이션 */}
          <div className="bd-tab-scroll">
            <nav className="bd-tabs">
              <button
                className={`bd-tab${activeTab === "info" ? " bd-tab--active" : ""}`}
                onClick={() => setActiveTab("info")}
              >
                사찰 정보
              </button>
              {temple.item_groups && temple.item_groups.map((group) => (
                <button
                  key={group.name}
                  className={`bd-tab${activeTab === group.name ? " bd-tab--active" : ""}`}
                  onClick={() => setActiveTab(group.name)}
                >
                  {group.name}
                </button>
              ))}
            </nav>
          </div>

          {/* 사찰 정보 탭 */}
          {activeTab === "info" && (
            <div className="bd-info-panel">
              <h2 className="bd-section-label">위치 정보</h2>
              <div className="bd-map-placeholder">
                <MdTempleBuddhist className="bd-map-ico" />
                <span>지도 API 연동 필요</span>
              </div>

              <h2 className="bd-section-label" style={{ marginTop: "24px" }}>연락처</h2>
              <div className="bd-contact-grid">
                <div className="bd-contact-item">
                  <FaPhone className="bd-contact-ico" />
                  <div>
                    <small>전화번호</small>
                    <strong>{temple.phone || "정보 없음"}</strong>
                  </div>
                </div>
                <div className="bd-contact-item">
                  <FaGlobe className="bd-contact-ico" />
                  <div>
                    <small>웹사이트</small>
                    <strong>{temple.website || "정보 없음"}</strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 공양 아이템 탭 */}
          {temple.item_groups && temple.item_groups.map((group) =>
            activeTab === group.name && (
              <div key={group.name} className="bd-items-panel">
                {group.items.map((item) => (
                  <div key={item.id} className="bd-item">
                    {item.images && item.images.length > 0 && (
                      <div
                        className="bd-item-img"
                        style={{ backgroundImage: `url(${item.images[0].url})` }}
                      />
                    )}
                    <div className="bd-item-body">
                      <h3 className="bd-item-name">{item.name}</h3>
                      {item.description && (
                        <p className="bd-item-desc">{item.description}</p>
                      )}
                      <div className="bd-item-footer">
                        <span className="bd-item-price">
                          {new Intl.NumberFormat("ko-KR").format(item.price)}원
                        </span>
                        <div className="bd-item-actions">
                          {/* 수량 조절 */}
                          <div className="bd-qty">
                            <button
                              className="bd-qty-btn"
                              onClick={() => handleQuantityChange(item.id, -1)}
                              disabled={!isAuthenticated || temple.is_preparing}
                            >−</button>
                            <span className="bd-qty-val">{itemQuantities[item.id] || 1}</span>
                            <button
                              className="bd-qty-btn"
                              onClick={() => handleQuantityChange(item.id, 1)}
                              disabled={!isAuthenticated || temple.is_preparing}
                            >+</button>
                          </div>
                          {/* 담기 / 로그인 */}
                          {temple.is_preparing ? (
                            <button className="bd-btn bd-btn--outline bd-btn--sm" disabled>
                              준비중
                            </button>
                          ) : isAuthenticated ? (
                            <button
                              className="bd-btn bd-btn--primary bd-btn--sm"
                              onClick={() => handleAddToCart(item)}
                            >
                              담기
                            </button>
                          ) : (
                            <button
                              className="bd-btn bd-btn--outline bd-btn--sm"
                              onClick={handleLogin}
                              title="로그인이 필요합니다"
                            >
                              <FaSignInAlt /> 로그인
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </Container>

      {/* 모바일 하단 고정 버튼 */}
      {isMobile && (
        <div className="bd-mobile-bar">
          <button className="bd-mobile-cart-btn" onClick={handleGoToCart}>
            {isAuthenticated ? (
              <>
                <FaShoppingCart />
                장바구니 보기
                {cartItemCount > 0 && <span className="bd-cart-badge">{cartItemCount}</span>}
              </>
            ) : (
              <>
                <FaSignInAlt />
                로그인하여 장바구니 이용하기
              </>
            )}
          </button>
        </div>
      )}

      {/* 토스트 알림 */}
      <ToastContainer
        position={isMobile ? "bottom-center" : "bottom-end"}
        className="p-3"
        style={{ zIndex: 1070 }}
      >
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          style={{
            borderLeft: "4px solid var(--main-color)",
            boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
            background: "#fff",
          }}
        >
          <Toast.Header closeButton>
            <strong className="me-auto" style={{ color: "var(--main-color)" }}>알림</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* ── 스타일 ── */}
      <style jsx="true">{`
        :root {
          --bd-teal: #009590;
          --bd-teal-dark: #007873;
          --bd-teal-pale: #e8f5f5;
          --bd-text: #2c3e35;
          --bd-muted: #6e8a84;
          --bd-border: #e4edec;
        }

        /* 로딩 */
        .bd-loading {
          text-align: center;
          padding: 100px 20px;
          color: var(--bd-muted);
        }
        .bd-loading p { margin-top: 18px; font-size: 0.92rem; }

        /* 상태 박스 (에러/없음) */
        .bd-status-box {
          text-align: center;
          padding: 60px 20px;
          border: 1px solid var(--bd-border);
          border-radius: 4px;
        }
        .bd-status-box--error { border-color: #f5c6cb; background: #fff8f8; }
        .bd-status-ico {
          font-size: 2.4rem;
          display: block;
          margin-bottom: 14px;
          color: #e74c3c;
        }
        .bd-status-ico--dharma { color: var(--bd-teal); opacity: 0.25; }
        .bd-status-box p { color: var(--bd-muted); margin-bottom: 20px; font-size: 0.93rem; }

        /* 스티키 헤더 */
        .bd-sticky-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 58px;
          background: rgba(255,255,255,0.96);
          backdrop-filter: blur(8px);
          border-bottom: 1px solid var(--bd-border);
          z-index: 100;
          transition: opacity 0.3s ease;
        }
        .bd-sticky-inner {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .bd-sticky-name {
          font-weight: 700;
          font-size: 1rem;
          color: var(--bd-text);
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* 뒤로가기 버튼 */
        .bd-back-btn {
          width: 36px; height: 36px;
          border-radius: 50%;
          border: 1px solid var(--bd-border);
          background: #fff;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          color: var(--bd-text);
          transition: background 0.2s;
          font-size: 0.9rem;
        }
        .bd-back-btn:hover { background: var(--bd-teal-pale); }

        /* 장바구니 버튼 (헤더) */
        .bd-cart-btn {
          position: relative;
          width: 36px; height: 36px;
          border-radius: 50%;
          border: 1px solid var(--bd-border);
          background: #fff;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          color: var(--bd-text);
          transition: background 0.2s;
          font-size: 1rem;
        }
        .bd-cart-btn:hover { background: var(--bd-teal-pale); }
        .bd-cart-lock {
          position: absolute;
          bottom: -2px; right: -2px;
          font-size: 0.55rem;
          color: #888;
        }
        .bd-cart-badge {
          position: absolute;
          top: -4px; right: -4px;
          background: #e74c3c;
          color: #fff;
          font-size: 0.6rem;
          font-weight: 700;
          width: 16px; height: 16px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
        }

        /* 갤러리 */
        .bd-gallery {
          position: relative;
          background: #1a1a1a;
          overflow: hidden;
          width: 100%;
          height: 450px;
        }
        @media (max-width: 767px) { .bd-gallery { height: 300px; } }

        .bd-gallery-back {
          position: absolute;
          top: 14px; left: 14px;
          z-index: 10;
          width: 36px; height: 36px;
          border-radius: 50%;
          background: rgba(0,0,0,0.4);
          border: none;
          color: #fff;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          font-size: 0.9rem;
          backdrop-filter: blur(4px);
          transition: background 0.2s;
        }
        .bd-gallery-back:hover { background: rgba(0,0,0,0.65); }

        .bd-gallery-slider {
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
        .bd-gallery-track {
          display: flex;
          height: 100%;
          transition: transform 0.4s ease;
        }
        .bd-gallery-slide {
          flex-shrink: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          position: relative;
        }
        .bd-gallery-slide::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          backdrop-filter: blur(15px);
          background: rgba(0, 0, 0, 0.5);
          z-index: 1;
        }
        .bd-gallery-slide::after {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background-image: inherit;
          background-size: contain;
          background-position: center;
          background-repeat: no-repeat;
          z-index: 2;
        }

        .bd-gallery-ctrl {
          position: absolute;
          top: 50%; transform: translateY(-50%);
          width: 36px; height: 36px;
          border-radius: 50%;
          background: rgba(0,0,0,0.35);
          border: none;
          color: #fff;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          font-size: 0.85rem;
          transition: background 0.2s;
          z-index: 5;
        }
        .bd-gallery-ctrl:hover { background: rgba(0,0,0,0.6); }
        .bd-gallery-ctrl:disabled { opacity: 0.3; }
        .bd-gallery-ctrl--prev { left: 12px; }
        .bd-gallery-ctrl--next { right: 12px; }

        .bd-gallery-dots {
          position: absolute;
          bottom: 12px;
          left: 50%; transform: translateX(-50%);
          display: flex; gap: 6px;
          z-index: 5;
        }
        .bd-gallery-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          border: none;
          background: rgba(255,255,255,0.45);
          cursor: pointer;
          padding: 0;
          transition: background 0.2s;
        }
        .bd-gallery-dot--active { background: #fff; }

        .bd-gallery-placeholder {
          width: 100%; height: 100%;
          display: flex; align-items: center; justify-content: center;
          font-size: 4rem;
          color: rgba(255,255,255,0.15);
        }

        /* 본문 */
        .bd-body {
          padding-top: 28px;
          padding-bottom: 48px;
        }
        .bd-wrap--mobile .bd-body { padding-bottom: 80px; }

        /* 카드 */
        .bd-card {
          background: #fff;
          border: 1px solid var(--bd-border);
          border-radius: 4px;
          padding: 28px 28px 24px;
        }
        @media (max-width: 767px) { .bd-card { padding: 20px 18px; } }

        .bd-card-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 24px;
        }

        .bd-type-badge {
          display: inline-block;
          font-size: 0.69rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          color: var(--bd-teal);
          border: 1px solid var(--bd-teal);
          padding: 2px 9px;
          border-radius: 2px;
          margin-bottom: 8px;
          opacity: 0.8;
        }

        .bd-temple-name {
          font-size: 1.7rem;
          font-weight: 700;
          color: var(--bd-text);
          margin-bottom: 10px;
          letter-spacing: -0.02em;
          line-height: 1.2;
        }
        @media (max-width: 767px) { .bd-temple-name { font-size: 1.35rem; } }

        .bd-addr {
          display: flex;
          align-items: flex-start;
          gap: 7px;
          font-size: 0.85rem;
          color: var(--bd-muted);
          margin: 0;
          line-height: 1.5;
        }
        .bd-addr-ico { flex-shrink: 0; margin-top: 2px; color: var(--bd-teal); }

        /* 섹션 라벨 */
        .bd-section-label {
          font-size: 0.88rem;
          font-weight: 700;
          color: var(--bd-teal);
          letter-spacing: 0.06em;
          text-transform: uppercase;
          margin-bottom: 14px;
        }

        /* 사찰 소개 */
        .bd-desc-wrap { margin-bottom: 24px; }
        .bd-desc-box {
          background: var(--bd-teal-pale);
          border-left: 3px solid var(--bd-teal);
          border-radius: 0 3px 3px 0;
          padding: 18px 20px;
        }
        .bd-desc-text {
          font-size: 0.92rem;
          color: #3d5a55;
          line-height: 1.85;
          margin: 0;
        }
        .bd-desc-toggle {
          background: none;
          border: none;
          color: var(--bd-teal);
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          margin-top: 10px;
          padding: 0;
          letter-spacing: 0.04em;
        }

        /* 로그인 안내 */
        .bd-login-notice {
          display: flex;
          align-items: center;
          gap: 10px;
          background: var(--bd-teal-pale);
          border-radius: 3px;
          padding: 12px 16px;
          font-size: 0.85rem;
          color: var(--bd-muted);
          margin-bottom: 24px;
        }
        .bd-login-notice-ico { color: var(--bd-teal); flex-shrink: 0; }
        .bd-login-link { color: var(--bd-teal); font-weight: 700; text-decoration: none; }
        .bd-login-link:hover { text-decoration: underline; }

        /* 탭 */
        .bd-tab-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          margin-bottom: 24px;
        }
        .bd-tab-scroll::-webkit-scrollbar { display: none; }

        .bd-tabs {
          display: flex;
          border-bottom: 1px solid var(--bd-border);
          min-width: max-content;
          gap: 0;
        }

        .bd-tab {
          padding: 10px 18px;
          border: none;
          background: transparent;
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--bd-muted);
          cursor: pointer;
          white-space: nowrap;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          transition: all 0.2s ease;
          font-family: 'Noto Sans KR', sans-serif;
        }
        .bd-tab:hover { color: var(--bd-teal); }
        .bd-tab--active {
          color: var(--bd-teal);
          font-weight: 700;
          border-bottom-color: var(--bd-teal);
        }

        /* 정보 패널 */
        .bd-info-panel {}
        .bd-map-placeholder {
          background: var(--bd-teal-pale);
          border-radius: 3px;
          height: 220px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: var(--bd-muted);
          font-size: 0.85rem;
        }
        .bd-map-ico { font-size: 2.2rem; color: var(--bd-teal); opacity: 0.25; }

        .bd-contact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        @media (max-width: 576px) { .bd-contact-grid { grid-template-columns: 1fr; } }

        .bd-contact-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          background: #f9fdfd;
          border: 1px solid var(--bd-border);
          border-radius: 3px;
        }
        .bd-contact-ico { font-size: 1.1rem; color: var(--bd-teal); flex-shrink: 0; }
        .bd-contact-item small { display: block; font-size: 0.72rem; color: var(--bd-muted); margin-bottom: 4px; }
        .bd-contact-item strong { font-size: 0.92rem; color: var(--bd-text); }

        /* 공양 아이템 */
        .bd-items-panel { display: flex; flex-direction: column; gap: 14px; }

        .bd-item {
          display: flex;
          gap: 18px;
          padding: 20px;
          border: 1px solid var(--bd-border);
          border-radius: 3px;
          background: #fff;
          transition: box-shadow 0.25s ease;
        }
        .bd-item:hover { box-shadow: 0 6px 20px rgba(0,149,144,0.08); }

        .bd-item-img {
          flex-shrink: 0;
          width: 90px; height: 90px;
          border-radius: 3px;
          background-size: cover;
          background-position: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        @media (max-width: 480px) {
          .bd-item { flex-direction: column; }
          .bd-item-img { width: 100%; height: 160px; margin: 0; }
        }

        .bd-item-body { flex: 1; }
        .bd-item-name {
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--bd-text);
          margin-bottom: 6px;
          letter-spacing: -0.01em;
        }
        .bd-item-desc {
          font-size: 0.84rem;
          color: var(--bd-muted);
          line-height: 1.65;
          margin-bottom: 14px;
        }
        .bd-item-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
        }
        .bd-item-price {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--bd-teal);
          letter-spacing: -0.01em;
        }
        .bd-item-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        /* 수량 조절 */
        .bd-qty {
          display: flex;
          align-items: center;
          gap: 0;
          border: 1px solid var(--bd-border);
          border-radius: 3px;
          overflow: hidden;
        }
        .bd-qty-btn {
          width: 32px; height: 32px;
          border: none;
          background: #f9fdfd;
          color: var(--bd-text);
          font-size: 1rem;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.2s;
        }
        .bd-qty-btn:hover:not(:disabled) { background: var(--bd-teal-pale); }
        .bd-qty-btn:disabled { opacity: 0.4; cursor: default; }
        .bd-qty-val {
          min-width: 32px;
          text-align: center;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--bd-text);
          border-left: 1px solid var(--bd-border);
          border-right: 1px solid var(--bd-border);
          line-height: 32px;
        }

        /* 버튼 */
        .bd-btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 10px 22px;
          border-radius: 3px;
          font-size: 0.9rem;
          font-weight: 600;
          letter-spacing: 0.03em;
          cursor: pointer;
          border: none;
          font-family: 'Noto Sans KR', sans-serif;
          transition: all 0.25s ease;
          text-decoration: none;
        }
        .bd-btn--primary { background: var(--bd-teal); color: #fff; }
        .bd-btn--primary:hover { background: var(--bd-teal-dark); transform: translateY(-1px); }
        .bd-btn--outline {
          background: transparent;
          color: var(--bd-teal);
          border: 1.5px solid var(--bd-teal);
        }
        .bd-btn--outline:hover { background: var(--bd-teal); color: #fff; }
        .bd-btn--sm { padding: 7px 16px; font-size: 0.82rem; }

        /* 모바일 하단 바 */
        .bd-mobile-bar {
          position: fixed;
          bottom: 0; left: 0; right: 0;
          background: #fff;
          border-top: 1px solid var(--bd-border);
          padding: 10px 16px;
          z-index: 50;
        }
        .bd-mobile-cart-btn {
          width: 100%;
          padding: 13px;
          background: var(--bd-teal);
          color: #fff;
          border: none;
          border-radius: 3px;
          font-size: 0.97rem;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: 'Noto Sans KR', sans-serif;
          position: relative;
          transition: background 0.25s;
        }
        .bd-mobile-cart-btn:hover { background: var(--bd-teal-dark); }
      `}</style>
    </div>
  );
};

const mapStateToProps = (state) => ({ cartItemCount: state.cart.itemCount });
const mapDispatchToProps = { addToCart, clearCart };

export default connect(mapStateToProps, mapDispatchToProps)(BuddhismDetail);